import { useState, useCallback } from 'react'
import type { Message } from './useChatMessages'

// 번역 기능 무료화 - 포인트 차감 없이 바로 번역
interface UseTranslationReturn {
  showTranslation: Record<number, boolean>
  handleTranslate: (messageId: number) => Promise<void>
}

export function useChatTranslation(
  messages: Message[],
  setMessages: React.Dispatch<React.SetStateAction<Message[]>>,
  userLocale: string
): UseTranslationReturn {
  const [showTranslation, setShowTranslation] = useState<Record<number, boolean>>({})

  const handleTranslate = useCallback(async (messageId: number) => {
    const message = messages.find((m) => m.id === messageId)
    if (!message) return

    // If already translated and showing, just toggle off
    if (showTranslation[messageId]) {
      setShowTranslation((prev) => ({ ...prev, [messageId]: false }))
      return
    }

    // If already has translation, just toggle on
    if (message.content_translated) {
      setShowTranslation((prev) => ({ ...prev, [messageId]: true }))
      return
    }

    // Execute translation (무료)
    try {
      const { translateWithFallback } = await import('../lib/browserTranslation')
      const targetLocale = userLocale
      const sourceLocale = message.original_locale || 'en'

      const result = await translateWithFallback(
        message.content,
        sourceLocale,
        targetLocale
      )

      setMessages((prev) =>
        prev.map((m) =>
          m.id === messageId
            ? {
              ...m,
              content_translated: result.translatedText,
              culture_hint: result.cultureHint || null,
            }
            : m
        )
      )

      setShowTranslation((prev) => ({ ...prev, [messageId]: true }))
    } catch {
      // Translation failed - silent
    }
  }, [messages, setMessages, userLocale, showTranslation])

  return {
    showTranslation,
    handleTranslate,
  }
}
